import React, { useState } from 'react';
import { X, ShoppingBag, Menu, Instagram, Facebook, Twitter } from 'lucide-react';
import { SITE_TEMPLATES } from '@/config/templates';

export default function TemplatePreview({ templateId, brandName, onClose }) {
  const template = SITE_TEMPLATES[templateId];
  const [activeSection, setActiveSection] = useState('hero');
  
  if (!template) return null;

  const { colors, fonts, defaultContent } = template;

  const previewStyles = {
    '--preview-primary': colors.primary,
    '--preview-secondary': colors.secondary,
    '--preview-accent': colors.accent,
    '--preview-bg': colors.background,
    '--preview-text': colors.text,
    '--preview-heading-font': fonts.heading,
    '--preview-body-font': fonts.body,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="relative w-full max-w-6xl h-[85vh] bg-white rounded-lg overflow-hidden shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 w-10 h-10 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
          data-testid="close-preview"
        >
          <X size={20} />
        </button>

        {/* Template Badge */}
        <div className="absolute top-4 left-4 z-50 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
          {template.preview} {template.name} Template
        </div>

        {/* Preview Content */}
        <div 
          className="h-full overflow-y-auto"
          style={previewStyles}
        >
          {/* Navigation Preview */}
          <nav 
            className="sticky top-0 z-40 border-b px-6 py-4"
            style={{ 
              backgroundColor: colors.background,
              borderColor: colors.text + '20',
              fontFamily: fonts.body
            }}
          >
            <div className="max-w-6xl mx-auto flex items-center justify-between">
              <span 
                className="text-xl font-medium"
                style={{ fontFamily: fonts.heading, color: colors.text }}
              >
                {brandName || 'Your Brand'}
              </span>
              <div className="flex items-center gap-6">
                <span className="text-sm" style={{ color: colors.text }}>Shop</span>
                <span className="text-sm" style={{ color: colors.text }}>About</span>
                <span className="text-sm" style={{ color: colors.text }}>Gallery</span>
                <ShoppingBag size={20} style={{ color: colors.text }} />
              </div>
            </div>
          </nav>

          {/* Hero Section Preview */}
          <section 
            className="relative h-[500px] flex items-center justify-center text-center"
            style={{ backgroundColor: colors.primary }}
          >
            <div className="absolute inset-0 bg-black/30" />
            <div className="relative z-10 text-white px-6">
              <h1 
                className="text-5xl md:text-6xl mb-4"
                style={{ fontFamily: fonts.heading }}
              >
                {brandName || 'Your Brand'}
              </h1>
              <p 
                className="text-xl tracking-widest mb-8 opacity-90"
                style={{ fontFamily: fonts.body }}
              >
                {defaultContent.hero.tagline}
              </p>
              <button 
                className="px-8 py-4 text-sm tracking-widest uppercase font-medium transition-all"
                style={{ 
                  backgroundColor: colors.background, 
                  color: colors.text,
                  fontFamily: fonts.body
                }}
              >
                {defaultContent.hero.button_text}
              </button>
            </div>
          </section>

          {/* Banner Preview - Only for templates with banners */}
          {defaultContent.banners?.length > 0 && (
          <section 
            className="h-[300px] flex items-center justify-center"
            style={{ backgroundColor: colors.secondary }}
          >
            <h2 
              className="text-4xl text-white"
              style={{ fontFamily: fonts.heading }}
            >
              {defaultContent.banners[0].text}
            </h2>
          </section>
          )}

          {/* Products Section Preview */}
          <section 
            className="py-20 px-6"
            style={{ backgroundColor: colors.background }}
          >
            <div className="max-w-6xl mx-auto">
              <h2 
                className="text-4xl text-center mb-4"
                style={{ fontFamily: fonts.heading, color: colors.text }}
              >
                Featured Collection
              </h2>
              <p 
                className="text-center mb-12 opacity-70"
                style={{ fontFamily: fonts.body, color: colors.text }}
              >
                Discover our curated selection
              </p>
              
              {/* Product Grid Placeholder */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="group cursor-pointer">
                    <div 
                      className="aspect-[4/5] mb-4"
                      style={{ backgroundColor: colors.accent }}
                    />
                    <p 
                      className="text-lg mb-1"
                      style={{ fontFamily: fonts.heading, color: colors.text }}
                    >
                      Product Name
                    </p>
                    <p 
                      className="opacity-70"
                      style={{ fontFamily: fonts.body, color: colors.text }}
                    >
                      $299.00
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* About Section Preview */}
          <section 
            className="py-20 px-6"
            style={{ backgroundColor: colors.background }}
          >
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div 
                  className="aspect-[4/5]"
                  style={{ backgroundColor: colors.accent }}
                />
                <div>
                  <h2 
                    className="text-4xl mb-6"
                    style={{ fontFamily: fonts.heading, color: colors.text }}
                  >
                    {defaultContent.about.title}
                  </h2>
                  <p 
                    className="mb-6 leading-relaxed opacity-80"
                    style={{ fontFamily: fonts.body, color: colors.text }}
                  >
                    {defaultContent.about.text}
                  </p>
                  <p 
                    className="italic opacity-60"
                    style={{ fontFamily: fonts.body, color: colors.text }}
                  >
                    "{defaultContent.about.vision}"
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Signup Section Preview */}
          <section 
            className="py-20 px-6"
            style={{ backgroundColor: colors.text }}
          >
            <div className="max-w-2xl mx-auto text-center">
              <h2 
                className="text-4xl mb-4"
                style={{ fontFamily: fonts.heading, color: colors.background }}
              >
                {defaultContent.signup.title}
              </h2>
              <p 
                className="mb-8 opacity-70"
                style={{ fontFamily: fonts.body, color: colors.background }}
              >
                {defaultContent.signup.subtitle}
              </p>
              <div className="flex gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-4 py-3 bg-transparent border"
                  style={{ 
                    borderColor: colors.background + '40',
                    color: colors.background,
                    fontFamily: fonts.body
                  }}
                />
                <button
                  className="px-6 py-3 text-sm uppercase tracking-wider"
                  style={{ 
                    backgroundColor: colors.primary,
                    color: '#fff',
                    fontFamily: fonts.body
                  }}
                >
                  Join
                </button>
              </div>
            </div>
          </section>

          {/* Footer Preview */}
          <footer 
            className="py-8 px-6 border-t"
            style={{ 
              backgroundColor: colors.background,
              borderColor: colors.text + '20'
            }}
          >
            <div className="max-w-6xl mx-auto flex items-center justify-between">
              <span style={{ fontFamily: fonts.heading, color: colors.text }}>
                {brandName || 'Your Brand'}
              </span>
              <div className="flex items-center gap-4">
                <Instagram size={18} style={{ color: colors.text }} />
                <Facebook size={18} style={{ color: colors.text }} />
                <Twitter size={18} style={{ color: colors.text }} />
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
